import { useEffect, useState } from 'react';
import { createPaciente, getPacienteInformeById, searchPacientesByName, updatePaciente } from '../api/pacientesApi';
import { mapFormDataToPayload, mapPacienteToFormData } from '../utils/pacienteMappers';

const initialFormData = {
  codHistorico: '',
  firstname: '',
  lastname: '',
  telefono: '',
  dateBirth: '',
  grupo: '',
  sexo: '',
  viven: '',
  nivelEducacional: '',
  grupoREVI: false,
  grupoEquilibrio: false,
  grupoSP: false,
  grupoVent: false,
};

const initialErrors = {
  firstname: '',
  lastname: '',
  telefono: '',
  dateBirth: '',
  grupo: '',
  sexo: '',
  viven: '',
  nivelEducacional: '',
};

export const usePacienteForm = ({ idPaciente, navigate }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [openGood, setOpenGood] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [duplicatePacientes, setDuplicatePacientes] = useState([]);
  const [searchingDuplicates, setSearchingDuplicates] = useState(false);

  useEffect(() => {
    if (!idPaciente) return;

    const fetchPaciente = async () => {
      try {
        const data = await getPacienteInformeById(idPaciente);
        setFormData(mapPacienteToFormData(data));
      } catch (error) {
        console.error('Error al cargar el paciente:', error);
        setOpenError(true);
      }
    };

    fetchPaciente();
  }, [idPaciente]);

  useEffect(() => {
    if (idPaciente) return;

    const firstname = formData.firstname;
    const lastname = formData.lastname;
    if (!firstname || !lastname || firstname.length < 2 || lastname.length < 2) {
      setDuplicatePacientes([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchingDuplicates(true);
      try {
        const duplicates = await searchPacientesByName(firstname);
        const filtered = duplicates.filter((paciente) => {
          const lastnameMatch =
            paciente.lastname?.toLowerCase().includes(lastname.toLowerCase()) ||
            lastname.toLowerCase().includes(paciente.lastname?.toLowerCase());
          return lastnameMatch;
        });
        setDuplicatePacientes(filtered);
      } catch (error) {
        console.error('Error al buscar duplicados:', error);
      } finally {
        setSearchingDuplicates(false);
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [formData.firstname, formData.lastname, idPaciente]);

  const handleChange = (event) => {
    let { name, value, type, checked } = event.target;
    if (name === 'grupo') {
      value = parseInt(value, 10);
    }
    const newValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const nextErrors = {
      firstname: formData.firstname ? '' : 'Nombre requerido',
      lastname: formData.lastname ? '' : 'Apellidos requeridos',
      telefono: formData.telefono ? '' : 'Telefono requerido',
      dateBirth: formData.dateBirth ? '' : 'Fecha de nacimiento requerida',
      grupo: formData.grupo !== '' ? '' : 'Grupo requerido',
      sexo: formData.sexo ? '' : 'Sexo requerido',
      viven: formData.viven ? '' : 'Estado requerido',
      nivelEducacional: formData.nivelEducacional ? '' : 'Nivel educacional requerido',
    };

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const savePaciente = async () => {
    try {
      const payload = mapFormDataToPayload(formData);
      if (idPaciente) {
        await updatePaciente({ idPaciente, payload });
      } else {
        await createPaciente(payload);
      }
      setOpenGood(true);
      setTimeout(() => navigate('/home'), 2000);
    } catch (error) {
      console.error('Error al guardar el paciente:', error);
      setOpenError(true);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!idPaciente && duplicatePacientes.length > 0) {
      setOpenDuplicateDialog(true);
      return;
    }
    await savePaciente();
  };

  return {
    formData,
    errors,
    openGood,
    setOpenGood,
    openError,
    setOpenError,
    openDuplicateDialog,
    setOpenDuplicateDialog,
    duplicatePacientes,
    searchingDuplicates,
    handleChange,
    handleSave,
    savePaciente,
  };
};
